// note: to creat arged listeners we need to change:
// const queue = [...this.#events.get(event)];
// to
// const queue = [...this.#events.get(event)].map((listener) => () => listener(...args));

class EventEmitter {
  #events = new Map();

  on(event, listener) {
    if (!this.#events.has(event)) {
      this.#events.set(event, new Set());
    }
    this.#events.get(event).add(listener);
  }

  off(event, listener) {
    if (this.#events.has(event)) {
      this.#events.get(event).delete(listener);
    }
  }

  #runQueue(queue) {
    function next() {
      const fn = queue.shift();
      if (fn) {
        fn();
        requestAnimationFrame(next);
      }
    }
    next();
  }

  emit(event) {
    if (!this.#events.has(event)) return;

    const queue = [...this.#events.get(event)];

    this.#runQueue(queue);
  }

  #debounceIds = new Map();
  debounce(debounceMs) {
    return (event, beforeQueue) => {
      if (!this.#events.has(event)) return;

      clearTimeout(this.#debounceIds.get(event));
      const id = setTimeout(() => {
        beforeQueue?.();
        const queue = [...this.#events.get(event)];

        this.#runQueue(queue);
      }, debounceMs);
      this.#debounceIds.set(event, id);
    };
  }

  #throttleState = new Map();
  throttle(throttleMs) {
    return (event, beforeQueue) => {
      if (!this.#events.has(event) || this.#throttleState.get(event)) return;

      this.#throttleState.set(event, true);

      beforeQueue?.();
      const queue = [...this.#events.get(event)];
      this.#runQueue(queue);

      setTimeout(() => {
        this.#throttleState.set(event, false);
      }, throttleMs);
    };
  }

  destroy() {
    this.#events.clear();
    this.#debounceIds.clear();
    this.#throttleState.clear();
  }
}

class ScrollHandler {
  #startScrollY = window.scrollY;
  #prevScrollY = window.scrollY;
  #scrollY = window.scrollY;
  #scrolling = false;

  #scrollCallbackThrottleMs = 150;
  #scrollEndDebounceMs = 300;

  #emitter = new EventEmitter();

  constructor({ scrollCallbackThrottleMs = 150, scrollEndDebounceMs = 300 } = {}) {
    this.#scrollCallbackThrottleMs = scrollCallbackThrottleMs;
    this.#scrollEndDebounceMs = scrollEndDebounceMs;

    this.#handleScrollBound = this.#handleScroll.bind(this);
    window.addEventListener('scroll', this.#handleScrollBound, { passive: true });

    this.#setState();
  }

  /**
   * Cleans up event listeners and internal state.
   */
  destroy() {
    window.removeEventListener('scroll', this.#handleScrollBound);
    this.#emitter.destroy();
  }

  /**
   * Registers a callback for scroll events.
   * @param {'scroll' | 'scrollend'} type - The event type to listen for
   * @param {Function} callback - Needs to be referentially stable, or be removed with `off` before adding again.
   */
  on(type, callback) {
    if (['scroll', 'scrollend'].includes(type) === false) {
      console.warn(`ScrollHandler: Unsupported event type "${type}"`);
      return;
    }

    this.#emitter.on(type, callback);
  }

  /**
   * Removes a previously registered callback.
   * @param {'scroll' | 'scrollend'} type - The event type to stop listening for - needs to match what was used in `on`.
   * @param {Function} callback - The callback to remove. Needs to be same reference as what was used in `on`.
   */
  off(type, callback) {
    if (['scroll', 'scrollend'].includes(type) === false) {
      console.warn(`ScrollHandler: Unsupported event type "${type}"`);
      return;
    }

    this.#emitter.off(type, callback);
  }

  reset() {
    this.#startScrollY = window.scrollY;
    this.#prevScrollY = window.scrollY;
    this.#scrollY = window.scrollY;
    this.#scrolling = false;
  }

  #prevDir = 0;
  #setState() {
    // We want to update `startScrollY` if the scroll direction changes.
    const dir = Math.sign(this.#scrollY - this.#prevScrollY);
    if (dir !== this.#prevDir) {
      this.#prevDir = dir;
      this.#startScrollY = this.#scrollY;
    }

    this.#prevScrollY = this.#scrollY;
    this.#scrollY = window.scrollY;
  }

  #handleScrollBound = null;
  #handleScroll() {
    if (!this.#scrolling) {
      this.#startScrollY = this.scrollY;
      this.#scrolling = true;
    }

    this.#setState();

    // Debounce end scroll event
    this.#emitter.debounce(this.#scrollEndDebounceMs)('scrollend');

    // Force update if we're at the top of the page
    if (this.#scrollY <= 0) {
      this.#emitter.emit('scroll');
    }

    // Throttle scroll events
    this.#emitter.throttle(this.#scrollCallbackThrottleMs)('scroll');
  }

  get state() {
    return {
      /**
       * The scroll position when the current scroll started.
       */
      startScrollY: this.#startScrollY,

      /**
       * The previous scroll position.
       */
      prevScrollY: this.#prevScrollY,

      scrollY: this.#scrollY,
      scrolling: this.#scrolling,

      /** -1, 0 or 1 */
      scrollDirection: Math.sign(this.#scrollY - this.#prevScrollY),
    };
  }
}

class ResizeHandler {
  node = null;

  #debounceMs = 300;
  #throttleMs = 150;

  #width = null;
  #height = null;
  #prevWidth = null;
  #prevHeight = null;

  #emitter = new EventEmitter();
  #observer = null;

  /**
   *
   * @param {Object} options
   * @param {HTMLElement} [options.node] - The node to observe for resize events.
   * @param {number} [options.debounceMs=300] - Debounce time in milliseconds for resize end events.
   * @param {number} [options.throttleMs=150] - Throttle time in milliseconds for resize events.
   */
  constructor({ node, debounceMs = 300, throttleMs = 150 } = {}) {
    this.node = node;
    this.#debounceMs = debounceMs;
    this.#throttleMs = throttleMs;

    this.#width = this.node ? this.node.offsetWidth : window.innerWidth;
    this.#height = this.node ? this.node.offsetHeight : window.innerHeight;
    this.#prevHeight = this.#height;
    this.#prevWidth = this.#width;

    this.#handleResizeBound = this.#handleResize.bind(this);

    window.addEventListener('resize', this.#handleResizeBound, { passive: true });

    if (this.node instanceof HTMLElement) {
      const resizeObserver = new ResizeObserver(this.#handleResizeBound);
      resizeObserver.observe(this.node);
      this.#observer = resizeObserver;
    }
  }

  destroy() {
    if (this.#observer) {
      this.#observer.unobserve(this.node);
      this.#observer.disconnect();
      this.#observer = null;
    } else {
      window.removeEventListener('resize', this.#handleResizeBound);
    }

    this.#emitter.destroy();
  }

  on(event, callback) {
    if (['resize', 'resizeend'].includes(event) === false) {
      console.warn(`ResizeHandler: Unsupported event type "${event}"`);
      return;
    }

    this.#emitter.on(event, callback);
  }

  off(event, callback) {
    if (['resize', 'resizeend'].includes(event) === false) {
      console.warn(`ResizeHandler: Unsupported event type "${event}"`);
      return;
    }

    this.#emitter.off(event, callback);
  }

  reset() {
    this.#setState();
  }

  #setState() {
    this.#prevWidth = this.#width;
    this.#prevHeight = this.#height;
    this.#width = this.node ? this.node.offsetWidth : window.innerWidth;
    this.#height = this.node ? this.node.offsetHeight : window.innerHeight;
  }

  get state() {
    return {
      previousWidth: this.#prevWidth,
      previousHeight: this.#prevHeight,
      width: this.#width,
      height: this.#height,
    };
  }

  #handleResizeBound = null;
  #handleResize() {
    // Emit resize event (throttled)
    this.#emitter.throttle(this.#throttleMs)('resize', () => this.#setState());

    // Emit resize end event (debounced)
    this.#emitter.debounce(this.#debounceMs)('resizeend', () => this.#setState());
  }
}

/**
 * BodyController
 *
 * Responsible for:
 * - Managing header bar show/hide based on scroll direction and thresholds
 * - Communicating header bar  visibility state to body attributes
 * - Managing sticky logo variant based on body height
 */
class BodyController extends HTMLElement {
  #headerBar = {
    node: null,
    selector: 'header-bar',
    changeEventName: 'header-bar:change',
  };

  #headerMenuOverlay = {
    node: null,
    selector: 'header-menu-overlay',
    changeEventName: 'header-menu-overlay:change',
  };

  attributes = {
    headerBarVisibilityState: 'data-header-bar-visibility-state',
    headerMenuOverlayState: 'data-header-menu-overlay-state',
  };

  // Event handler references

  #scrollHandler = null;
  #resizeHandler = null;

  constructor() {
    super();
    this.#scrollHandler = new ScrollHandler();
    this.#resizeHandler = new ResizeHandler({ node: this });
  }

  connectedCallback() {
    // Inert state management: add inert when URL has a hash or BIS_frame is visible
    this.#handleHashChangeBound = this.#updateInertState.bind(this);
    window.addEventListener('hashchange', this.#handleHashChangeBound);

    // Observe BIS_frame iframe for display changes
    this.#observeBISFrame();

    // Observe cookiebanner for display changes
    this.#observeCookiebanner();

    this.#updateInertState(); // Check initial state

    // Set an event listener on the header bar
    // to receive updates about method, position, and visibility changes
    customElements.whenDefined(this.#headerBar.selector).then(() => {
      this.#headerBar.node = this.querySelector(this.#headerBar.selector);

      if (!this.#headerBar.node) return;

      // Listen for sticky header state changes

      if (this.#headerBar.node.isValidEventName(this.#headerBar.changeEventName) === false) {
        console.warn(
          `BodyController: The header bar component does not support the "${this.#headerBar.changeEventName}" event.`
        );
        return;
      }

      this.#handleHeaderBarChangeBound = this.#handleHeaderBarChange.bind(this);
      this.#headerBar.node.addEventListener(this.#headerBar.changeEventName, this.#handleHeaderBarChangeBound);
    });

    customElements.whenDefined(this.#headerMenuOverlay.selector).then(() => {
      this.#headerMenuOverlay.node = this.querySelector(this.#headerMenuOverlay.selector);
      if (!this.#headerMenuOverlay.node) return;

      if (this.#headerMenuOverlay.node.isValidEventName(this.#headerMenuOverlay.changeEventName) === false) {
        console.warn(
          `BodyController: The header menu overlay component does not support the "${this.#headerMenuOverlay.changeEventName}" event.`
        );
        return;
      }

      this.#handleHeaderMenuOverlayChangeBound = this.#handleHeaderMenuOverlayChange.bind(this);
      this.#headerMenuOverlay.node.addEventListener(
        this.#headerMenuOverlay.changeEventName,
        this.#handleHeaderMenuOverlayChangeBound
      );
    });

    this.#handleResizeBound = this.#handleResize.bind(this);
    this.#resizeHandler.on('resizeend', this.#handleResizeBound);
  }

  disconnectedCallback() {
    this.#resizeHandler.destroy();
    this.#scrollHandler.destroy();

    window.removeEventListener('hashchange', this.#handleHashChangeBound);

    if (this.#bisFrameObserver) {
      this.#bisFrameObserver.disconnect();
      this.#bisFrameObserver = null;
    }

    if (this.#bodyObserver) {
      this.#bodyObserver.disconnect();
      this.#bodyObserver = null;
    }

    if (this.#bisFrameContentObserver) {
      this.#bisFrameContentObserver.disconnect();
      this.#bisFrameContentObserver = null;
    }

    if (this.#cookiebannerObserver) {
      this.#cookiebannerObserver.disconnect();
      this.#cookiebannerObserver = null;
    }

    if (this.#headerBar.node) {
      this.#headerBar.node.removeEventListener(this.#headerBar.changeEventName, this.#handleHeaderBarChangeBound);
    }

    if (this.#headerMenuOverlay.node) {
      this.#headerMenuOverlay.node.removeEventListener(
        this.#headerMenuOverlay.changeEventName,
        this.#handleHeaderBarChangeBound
      );
    }
  }

  #handleHashChangeBound = null;
  #bisFrameObserver = null;
  #bodyObserver = null;
  #cookiebannerObserver = null;

  #observeCookiebanner() {
    const cookiebanner = document.getElementById('cookiebanner');

    if (cookiebanner) {
      this.#attachCookiebannerObserver(cookiebanner);
    } else {
      // Cookiebanner might be added dynamically, observe body for its addition
      if (!this.#bodyObserver) {
        this.#bodyObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node.id === 'cookiebanner') {
                this.#attachCookiebannerObserver(node);
                return;
              }
              if (node.id === 'BIS_frame') {
                this.#attachBISFrameObserver(node);
                return;
              }
            }
          }
        });
        this.#bodyObserver.observe(document.body, { childList: true });
      }
    }
  }

  #attachCookiebannerObserver(cookiebanner) {
    this.#cookiebannerObserver = new MutationObserver(() => {
      this.#updateInertState();
    });
    this.#cookiebannerObserver.observe(cookiebanner, {
      attributes: true,
      attributeFilter: ['style'],
    });
    this.#updateInertState();
  }

  #isCookiebannerVisible() {
    const cookiebanner = document.getElementById('cookiebanner');
    if (!cookiebanner) return false;

    const style = cookiebanner.style.display;
    return style === 'block';
  }

  #observeBISFrame() {
    const bisFrame = document.getElementById('BIS_frame');

    if (bisFrame) {
      this.#attachBISFrameObserver(bisFrame);
    } else {
      // BIS_frame might be added dynamically, use shared body observer
      if (!this.#bodyObserver) {
        this.#bodyObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node.id === 'BIS_frame') {
                this.#attachBISFrameObserver(node);
                return;
              }
              if (node.id === 'cookiebanner') {
                this.#attachCookiebannerObserver(node);
                return;
              }
            }
          }
        });
        this.#bodyObserver.observe(document.body, { childList: true });
      }
    }
  }

  #attachBISFrameObserver(bisFrame) {
    this.#bisFrameObserver = new MutationObserver(() => {
      this.#updateInertState();
      this.#fixBISFrameAccessibility(bisFrame);
    });
    // Watch for style, class, hidden, and popover attribute changes
    this.#bisFrameObserver.observe(bisFrame, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'popover'],
    });
    // Check state immediately after attaching
    this.#updateInertState();
  }

  #bisFrameContentObserver = null;

  #fixBISFrameAccessibility(bisFrame) {
    if (!this.#isBISFrameVisible()) return;

    try {
      const iframeDoc = bisFrame.contentDocument || bisFrame.contentWindow?.document;
      if (!iframeDoc) return;

      // Fix close button accessibility (span with role="button" needs tabindex)
      const closeButton = iframeDoc.querySelector('.action-close');
      if (closeButton && !closeButton.hasAttribute('tabindex')) {
        closeButton.setAttribute('tabindex', '0');
      }

      // If content isn't ready yet, observe for changes
      if (!closeButton && !this.#bisFrameContentObserver) {
        this.#bisFrameContentObserver = new MutationObserver(() => {
          const btn = iframeDoc.querySelector('.action-close');
          if (btn && !btn.hasAttribute('tabindex')) {
            btn.setAttribute('tabindex', '0');
            this.#bisFrameContentObserver?.disconnect();
            this.#bisFrameContentObserver = null;
          }
        });
        this.#bisFrameContentObserver.observe(iframeDoc.body || iframeDoc, {
          childList: true,
          subtree: true,
        });
      }
    } catch (e) {
      // Cross-origin iframe, cannot access content
    }
  }

  #isBISFrameVisible() {
    const bisFrame = document.getElementById('BIS_frame');
    if (!bisFrame) return false;

    // Check if element is hidden via attribute
    if (bisFrame.hidden) return false;

    // Check popover state if using Popover API
    if (bisFrame.hasAttribute('popover') && !bisFrame.matches(':popover-open')) {
      return false;
    }

    const style = getComputedStyle(bisFrame);
    // Check multiple visibility indicators:
    // - display is not 'none'
    // - visibility is not 'hidden'
    // - opacity is greater than 0
    const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity || '1') > 0;

    // Also check if iframe has meaningful dimensions (positioned on screen)
    const hasSize = bisFrame.offsetWidth > 0 && bisFrame.offsetHeight > 0;

    return isVisible && hasSize;
  }

  #isOverlayHash() {
    const hash = window.location.hash;
    if (!hash) return false;

    // Ignore tracking hashes (contain = or known tracking prefixes)
    const hashValue = hash.substring(1);
    if (hashValue.includes('=') || hashValue.startsWith('msdynmkt_')) {
      return false;
    }

    // Check if any overlay is actually open with this hash
    const overlayContainer = document.querySelector('.overlay-container.is-open');
    return overlayContainer !== null;
  }

  #updateInertState() {
    if (this.#isOverlayHash() || this.#isBISFrameVisible() || this.#isCookiebannerVisible()) {
      this.setAttribute('inert', '');
    } else {
      this.removeAttribute('inert');
    }
  }

  /**
   * @typedef {Object} BodyControllerState
   * @property {number} scrollY - Current vertical scroll position
   * @property {number} previousScrollY - Previous vertical scroll position
   * @property {number} startScrollY - Scroll position at the start of the current scroll action
   * @property {number} scrollDirection - Scroll direction: 1 = down, -1 = up, 0 = no movement
   * @property {boolean} scrolling - Whether the user is currently scrolling
   * @property {number} height - Current root height
   * @property {number} width - Current root width
   * @property {number} previousHeight - Previous root height
   * @property {number} previousWidth - Previous root width
   */

  /**
   * @type {BodyControllerState}
   */
  get state() {
    return {
      scrollY: this.#scrollHandler.state.scrollY,
      previousScrollY: this.#scrollHandler.state.prevScrollY,
      startScrollY: this.#scrollHandler.state.startScrollY,
      scrollDirection: this.#scrollHandler.state.scrollDirection,
      scrolling: this.#scrollHandler.state.scrolling,
      height: this.#resizeHandler.state.height,
      width: this.#resizeHandler.state.width,
      previousHeight: this.#resizeHandler.state.previousHeight,
      previousWidth: this.#resizeHandler.state.previousWidth,
    };
  }

  on(type, callback) {
    if (type.startsWith('scroll')) {
      this.#scrollHandler.on(type, callback);
      return;
    }
    if (type.startsWith('resize')) {
      this.#resizeHandler.on(type, callback);
      return;
    }

    console.warn(`BodyController: Unsupported event type "${type}"`);
  }

  off(type, callback) {
    if (type.startsWith('scroll')) {
      this.#scrollHandler.off(type, callback);
      return;
    }
    if (type.startsWith('resize')) {
      this.#resizeHandler.off(type, callback);
      return;
    }

    console.warn(`BodyController: Unsupported event type "${type}"`);
  }

  #handleResizeBound = null;
  #handleResize() {
    this.#scrollHandler.reset();
  }

  #handleHeaderBarChangeBound = null;
  #headerBarValuesCache = {
    visibility: null,
    menu: null,
  };
  #handleHeaderBarChange(event) {
    if (!this.#headerBar.node) return;

    const { type, value } = event.detail;

    switch (type) {
      // We need to sync the visibility state to the body attribute
      // so both the sticky-header and other components depending on it
      // can style themselves correctly
      case 'visibility':
        if (this.#headerBarValuesCache.visibility === value) return;
        this.#headerBarValuesCache.visibility = value;
        document.body.setAttribute(this.attributes.headerBarVisibilityState, value);
        break;
    }
  }

  #handleHeaderMenuOverlayChangeBound = null;
  #headerMenuOverlayValuesCache = {
    state: null,
  };
  #handleHeaderMenuOverlayChange(event) {
    if (!this.#headerMenuOverlay.node) return;
    const open = event.detail.open;

    const value = open ? 'open' : 'closed';
    if (this.#headerMenuOverlayValuesCache.state === value) return;
    this.#headerMenuOverlayValuesCache.state = value;
    document.body.setAttribute(this.attributes.headerMenuOverlayState, value);
  }
}

customElements.define('body-controller', BodyController);
